import React, { useEffect, useMemo } from 'react';
import { useAPIWrapper, DetailBySchema, DetailFieldLayout } from 'drf-react-by-schema';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';

import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import PauseIcon from '@mui/icons-material/Pause';
import HomeIcon from '@mui/icons-material/Home';

import { appDispatch, dateFormatted, getAzeiteOrOleoName } from 'src/utils/utils';
import { useAppSelector } from 'src/app/hooks';
import { setPage } from 'src/features/pageSlice';

import { detailAccordion, detailDadosBasicos, detailNonAccordion } from 'src/styles/layout';
import { Layout } from 'src/styles';
import { AzeiteOrOleoProductionStatus, getAzeiteOrOleoProductionLabel } from 'src/constants';

import AsmubipButton from 'src/components/AsmubipButton';
import { PageType } from 'src/constants/PageType';

const MODEL = 'azeite_or_oleo_production';

export default function AzeiteOrOleoProductionDetail() {
  const { id, formValues, key, isMobile } = useAppSelector((state) => state.page);
  const { pageForm, loadSinglePageData } = useAPIWrapper();
  const navigate = useNavigate();
  const [URLSearchParams] = useSearchParams();
  const savedType = URLSearchParams.get('type');

  const name = useMemo(() => {
    return getAzeiteOrOleoName({ pageKey: key });
  }, [key]);

  const status = useMemo(() => {
    return formValues?.status?.value as AzeiteOrOleoProductionStatus | undefined;
  }, [formValues?.status]);

  const statusName = useMemo(() => {
    if (!status) {
      return;
    }
    return getAzeiteOrOleoProductionLabel({ azeiteOrOleoName: name, status });
  }, [status, name]);

  const statusHelper = useMemo(() => {
    if (!status) {
      return;
    }
    return getAzeiteOrOleoProductionLabel({
      azeiteOrOleoName: name,
      status,
      helperText: true,
    });
  }, [status, name]);

  const fieldsLayout = useMemo(() => {
    if (!status) {
      return;
    }

    const layoutPesagem =
      status === AzeiteOrOleoProductionStatus.COMPLETE ? detailAccordion : detailNonAccordion;

    const newFieldsLayout: DetailFieldLayout[] = [
      {
        title: 'Dados do registro',
        rows: ['lote', 'data_registro', 'perfil', 'ajudantes', 'grupo'],
        isAccordion: true,
        ...detailDadosBasicos,
      },
    ];

    if (status === AzeiteOrOleoProductionStatus.COMPLETE) {
      newFieldsLayout.push({
        title: 'Dados da produção',
        rows: ['data_armazenamento', 'volume_produzido'],
        ...detailNonAccordion,
      });
    }

    const layoutQuebra = {
      title: 'Dados da pesagem da amêndoa',
      rows: ['peso_amendoas', 'data_quebra', 'perfil'],
      isAccordion: status === AzeiteOrOleoProductionStatus.COMPLETE,
      isAccordionOpen: true,
      ...layoutPesagem,
    };
    newFieldsLayout.push(layoutQuebra);

    return newFieldsLayout;
  }, [status]);

  const handleEditClick = () => {
    navigate(`/${key}/edit/${id}`);
  };

  const handleInicioClick = () => {
    navigate('/');
  };

  useEffect(() => {
    if (!id || !key) {
      return;
    }
    const loadAllDataAsync = async () => {
      const values = await loadSinglePageData({
        model: MODEL,
        objId: id,
      });
      if (values) {
        const pageTitle = !savedType
          ? getAzeiteOrOleoName({ pageKey: key, title: true })
          : savedType === PageType.CREATE
            ? 'Produção criada!'
            : savedType === PageType.EDIT
              ? 'Produção atualizada!'
              : 'Produção finalizada!';
        appDispatch(
          setPage({
            id,
            model: MODEL,
            formValues: values,
            title: pageTitle,
          })
        );
      }
    };
    loadAllDataAsync();
  }, [id, key]);

  if (
    !pageForm?.schema ||
    !pageForm.columns ||
    !formValues ||
    !id ||
    !status ||
    !statusName ||
    !statusHelper
  ) {
    return null;
  }

  return (
    <>
      {savedType ? (
        <Card>
          <CardContent sx={Layout.productionDetailCardContent}>
            <Box sx={Layout.productionDetailCardIconWrapper}>
              {status === AzeiteOrOleoProductionStatus.COMPLETE ? (
                <CheckIcon sx={Layout.productionCardIcon} />
              ) : (
                <PauseIcon sx={Layout.productionCardIcon} />
              )}
            </Box>
            <Typography variant="h6">
              Produção de {getAzeiteOrOleoName({ pageKey: key, caps: true })}{' '}
              {dateFormatted(formValues.data_quebra)}
            </Typography>
            <Typography variant="subtitle1">{statusHelper}</Typography>
          </CardContent>
          <CardActions>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              sx={Layout.fullWidthButton}
              onClick={handleInicioClick}>
              Início
            </Button>
          </CardActions>
        </Card>
      ) : (
        <Container>
          <Card sx={Layout.BottomInfoContainerCard}>
            <CardContent>
              <Typography variant="subtitle1">{statusName}</Typography>
              <Typography variant="body2">{statusHelper}</Typography>
            </CardContent>
          </Card>
        </Container>
      )}
      <DetailBySchema
        schema={pageForm.schema}
        columns={pageForm.columns}
        values={formValues}
        fieldsLayout={fieldsLayout}
        elevation={0}
      />
      <Box
        sx={
          isMobile
            ? undefined
            : { display: 'flex', justifyContent: 'flex-end', paddingBottom: 4, marginTop: 2 }
        }>
        {status === AzeiteOrOleoProductionStatus.COMPLETE ? (
          <AsmubipButton
            variant="contained-secondary"
            fullWidth={isMobile}
            aria-label="editar"
            size="large"
            onClick={handleEditClick}
            startIcon={<EditIcon />}>
            Editar
          </AsmubipButton>
        ) : (
          <AsmubipButton
            variant="contained-dark"
            fullWidth={isMobile}
            aria-label="armazenar"
            size="large"
            onClick={handleEditClick}>
            Finalizar a produção
          </AsmubipButton>
        )}
      </Box>
      {/*isMobile && (
        <Container sx={Layout.BottomInfoContainer}>
          <Card sx={Layout.BottomInfoContainerCard}>
            <CardContent>
              <Typography variant="subtitle1">{statusName}</Typography>
              <Typography variant="body2">{statusHelper}</Typography>
            </CardContent>
          </Card>
        </Container>
      )*/}
    </>
  );
}
